import React, { useState, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { Profile } from '../../types';
import { departamentosDaPessoa, rotuloFuncaoComercial } from '../lib/departamentos';

/**
 * Valor sentinela usado na seleção para "itens sem responsável atribuído".
 * Não colide com id de profile (uuid). Só aparece quando `allowUnassigned` é true.
 */
export const SEM_RESPONSAVEL = 'unassigned';

interface ResponsibleFilterProps {
  selectedResponsibles: string[];
  setSelectedResponsibles: (ids: string[]) => void;
  profiles: Profile[];
  currentUserProfile: Profile | null;
  /** Pass 'tarefas' to enable the 'Minhas tarefas' toggle */
  viewContext?: 'crm' | 'gestao' | 'tarefas';
  className?: string; // Optional wrapper class
  /** Custom label for the filter button. Defaults to 'Responsável' */
  label?: string;
  /** Texto da opção "limpar seleção" (lista vazia). Defaults to 'Todos os responsáveis' */
  allLabel?: string;
  /** Lado de abertura do dropdown no desktop. 'right' ancora à direita do botão (abre para a esquerda). Defaults to 'left' */
  align?: 'left' | 'right';
  /** Quando true, ignora restrições de papel/parceiro e sempre mostra todos os responsáveis ativos (ex: tela de Solicitações) */
  unrestricted?: boolean;
  /** Quando true, adiciona a opção "Sem responsável" (valor `SEM_RESPONSAVEL`), combinável com as demais */
  allowUnassigned?: boolean;
}

const ResponsibleFilter: React.FC<ResponsibleFilterProps> = ({
  selectedResponsibles,
  setSelectedResponsibles,
  profiles,
  currentUserProfile,
  viewContext = 'crm',
  className = '',
  label = 'Responsável',
  allLabel = 'Todos os responsáveis',
  align = 'left',
  unrestricted = false,
  allowUnassigned = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedDept, setExpandedDept] = useState<string | null>(null);

  // Pending state — accumulates changes until user clicks "Aplicar"
  const [pendingSelection, setPendingSelection] = useState<string[]>([...selectedResponsibles]);

  // Sync pending selection when the dropdown opens
  useEffect(() => {
    if (isOpen) {
      setPendingSelection([...selectedResponsibles]);
    }
  }, [isOpen]);

  // If user is Vendedor, Vendedor Parceiro, or Parceiro, sometimes we restrict filter options
  const isVendedor = currentUserProfile?.role === 'vendedor';
  const isParceiro = currentUserProfile?.role === 'parceiro';
  const isVendedorParceiro = currentUserProfile?.role === 'vendedor_parceiro';
  
  if (!unrestricted && (isVendedor || isVendedorParceiro)) {
      if (viewContext === 'tarefas') return null;
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const filteredProfiles = profiles.filter(p => {
    if (p.status === 'inactive') return false;
    if (unrestricted) return true;
    if (currentUserProfile?.role === 'parceiro') {
      return p.parceiro_id === currentUserProfile.id || p.id === currentUserProfile.id;
    }
    if (currentUserProfile?.role === 'gerente_parcerias') {
      return p.role === 'parceiro' || p.role === 'vendedor_parceiro' || p.id === currentUserProfile.id;
    }
    return true;
  });

  const grouped: Record<string, typeof filteredProfiles> = {};
  filteredProfiles.forEach(p => {
    // Multi-departamento com fallback do campo antigo; nomes antigos do Comercial
    // (Interno/Externo) já saem como "Comercial".
    const proprios = departamentosDaPessoa(p);
    const depts = proprios.length > 0 ? proprios : ['Sem departamento'];
    depts.forEach(dept => {
      if (!grouped[dept]) grouped[dept] = [];
      // Avoid duplicates in same dept group
      if (!grouped[dept].find(x => x.id === p.id)) grouped[dept].push(p);
    });
  });

  const deptKeys = Object.keys(grouped).sort((a, b) => {
    if (a === 'Sem departamento') return 1;
    if (b === 'Sem departamento') return -1;
    return a.localeCompare(b);
  });

  // Check if pending differs from applied
  const hasChanges = (() => {
    if (pendingSelection.length !== selectedResponsibles.length) return true;
    const sortedPending = [...pendingSelection].sort();
    const sortedSelected = [...selectedResponsibles].sort();
    return sortedPending.some((v, i) => v !== sortedSelected[i]);
  })();

  const handleApply = () => {
    setSelectedResponsibles([...pendingSelection]);
    setIsOpen(false);
    setExpandedDept(null);
  };

  const handleCancel = () => {
    setPendingSelection([...selectedResponsibles]);
    setIsOpen(false);
    setExpandedDept(null);
  };

  return (
    <div className={`relative w-full md:w-auto ${className}`}>
      <button
        onClick={handleToggle}
        className={`flex items-center gap-2 px-3 py-2.5 md:py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full shadow-sm hover:border-gray-300 dark:hover:border-gray-500 transition-all w-full md:w-auto justify-between group
        ${isOpen ? 'ring-2 ring-primary/10 border-primary' : ''}
        `}
      >
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-600 flex items-center justify-center text-[10px] text-gray-500 dark:text-gray-300 font-bold group-hover:bg-primary/10 group-hover:text-primary transition-colors shrink-0">
            {label.charAt(0).toUpperCase()}
          </div>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate max-w-[110px]">
            {selectedResponsibles.length === 0 ? label :
              selectedResponsibles.length === 1 ? '1 selecionado' :
                `${selectedResponsibles.length} selecionados`}
          </span>
        </div>
        <ChevronDown size={13} className={`text-gray-400 transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Hover Flyout Dropdown */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-[60] bg-black/30 md:bg-transparent" onClick={handleCancel}></div>
          <div className={`fixed bottom-0 left-0 right-0 md:absolute md:bottom-auto md:top-full ${align === 'right' ? 'md:left-auto md:right-0' : 'md:left-0 md:right-auto'} mt-0 md:mt-2 w-full md:w-56 bg-white dark:bg-gray-800 rounded-t-2xl md:rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 z-[70] animate-fade-in md:overflow-visible flex flex-col max-h-[85vh] md:max-h-[60vh] pb-4 md:pb-0`}>
            <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto my-3 md:hidden flex-shrink-0"></div>
            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4 mb-2 md:hidden shrink-0">{label}</p>

            {/* "Todos" & "Minhas" options */}
            <div className="px-1.5 pb-1 flex-shrink-0">
              <label className={`flex items-center gap-3 px-3 py-3 md:py-2.5 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${pendingSelection.length === 0 ? 'bg-gray-50 dark:bg-gray-700' : ''}`}>
                <input
                  type="checkbox"
                  checked={pendingSelection.length === 0}
                  onChange={() => setPendingSelection([])}
                  className="w-5 h-5 md:w-4 md:h-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-500 dark:bg-gray-700"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{allLabel}</span>
              </label>

              {viewContext === 'tarefas' && (
                <label className={`flex items-center gap-3 px-3 py-3 md:py-2.5 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${pendingSelection.includes('me') ? 'bg-gray-50 dark:bg-gray-700' : ''}`}>
                  <input
                    type="checkbox"
                    checked={pendingSelection.includes('me')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setPendingSelection([...pendingSelection, 'me']);
                      } else {
                        setPendingSelection(pendingSelection.filter(id => id !== 'me'));
                      }
                    }}
                    className="w-5 h-5 md:w-4 md:h-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-500 dark:bg-gray-700"
                  />
                  <span className="text-sm font-bold text-primary dark:text-primary">Minhas tarefas</span>
                </label>
              )}

              {allowUnassigned && (
                <label className={`flex items-center gap-3 px-3 py-3 md:py-2.5 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${pendingSelection.includes(SEM_RESPONSAVEL) ? 'bg-gray-50 dark:bg-gray-700' : ''}`}>
                  <input
                    type="checkbox"
                    checked={pendingSelection.includes(SEM_RESPONSAVEL)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setPendingSelection([...pendingSelection, SEM_RESPONSAVEL]);
                      } else {
                        setPendingSelection(pendingSelection.filter(id => id !== SEM_RESPONSAVEL));
                      }
                    }}
                    className="w-5 h-5 md:w-4 md:h-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-500 dark:bg-gray-700"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Sem responsável</span>
                </label>
              )}

              <div className="h-px bg-gray-100 dark:bg-gray-700 my-1 mx-2"></div>
            </div>

            {/* Department list — Scrollable section */}
            <div className="flex-1 overflow-y-auto px-1.5 pb-2 custom-scrollbar md:pb-1.5 md:overflow-visible">
              {deptKeys.map(dept => {
                const deptCount = grouped[dept].filter(p => pendingSelection.includes(p.id)).length;
                const isExpanded = expandedDept === dept;
                
                return (
                  <div
                    key={dept}
                    className="relative"
                    onMouseEnter={() => setExpandedDept(dept)}
                    onMouseLeave={() => setExpandedDept(null)}
                  >
                    <button
                      onClick={() => setExpandedDept(isExpanded ? null : dept)}
                      className={`w-full text-left px-3 py-3 md:py-2.5 rounded-xl text-sm transition-all flex items-center justify-between gap-2
                          ${isExpanded
                          ? 'bg-primary/5 dark:bg-primary/10 text-primary font-bold'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium'
                        }
                      `}
                    >
                      <span className="truncate">{dept}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {deptCount > 0 && (
                          <span className="px-1.5 py-0.5 text-[9px] font-bold bg-primary/10 text-primary rounded-full">{deptCount}</span>
                        )}
                        <ChevronDown size={12} className={`text-gray-400 md:block hidden ${align === 'right' ? 'rotate-90' : '-rotate-90'}`} />
                        <ChevronDown size={12} className={`text-gray-400 transition-transform md:hidden ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </button>

                    {/* Desktop: Flyout to the right */}
                    {isExpanded && (
                      <div className={`hidden md:block absolute top-0 z-[80] ${align === 'right' ? 'right-full pr-1' : 'left-full pl-1'}`} style={{ pointerEvents: 'auto' }}>
                        <div className="w-64 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 p-1.5 max-h-[50vh] overflow-y-auto custom-scrollbar">
                          <p className="text-[10px] font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest px-3 pt-1 pb-1.5">{dept}</p>
                          {grouped[dept].sort((a, b) => a.full_name.localeCompare(b.full_name)).map(userProfile => (
                            <label key={userProfile.id} className="flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                              <input
                                type="checkbox"
                                checked={pendingSelection.includes(userProfile.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setPendingSelection(prev => [...prev, userProfile.id]);
                                  } else {
                                    setPendingSelection(prev => prev.filter(id => id !== userProfile.id));
                                  }
                                }}
                                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-500 dark:bg-gray-700 shrink-0"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-200 truncate">{userProfile.full_name}{rotuloFuncaoComercial(userProfile) && <span className="ml-1 text-[11px] text-gray-400">· {rotuloFuncaoComercial(userProfile)}</span>}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Mobile: inline expand below */}
                    {isExpanded && (
                      <div className="md:hidden pl-4 border-l-2 border-primary/20 ml-3 my-1 space-y-0.5">
                        {grouped[dept].sort((a, b) => a.full_name.localeCompare(b.full_name)).map(userProfile => (
                          <label key={userProfile.id} className="flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            <input
                              type="checkbox"
                              checked={pendingSelection.includes(userProfile.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setPendingSelection(prev => [...prev, userProfile.id]);
                                } else {
                                  setPendingSelection(prev => prev.filter(id => id !== userProfile.id));
                                }
                              }}
                              className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-500 dark:bg-gray-700 shrink-0"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-200 truncate">{userProfile.full_name}{rotuloFuncaoComercial(userProfile) && <span className="ml-1 text-[11px] text-gray-400">· {rotuloFuncaoComercial(userProfile)}</span>}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Apply / Cancel Footer */}
            <div className="flex-shrink-0 px-3 pt-2 pb-3 md:pb-3 border-t border-gray-100 dark:border-gray-700 mt-1">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCancel}
                  className="flex-1 px-3 py-2 md:py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleApply}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 md:py-1.5 text-sm font-bold rounded-lg transition-all
                    ${hasChanges
                      ? 'bg-primary text-white hover:bg-primary/90 shadow-sm'
                      : 'bg-primary/60 text-white/80 cursor-default'
                    }
                  `}
                >
                  <Check size={14} />
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ResponsibleFilter;
